import { AdminApiError, type AdminClient } from '@apx/admin-sdk';
import { STAKE_GRANULARITY_BY_GAME, wingoMultipliersSchema } from '@apx/integrator-config/schemas';
import { askPrompt } from '../modal.js';
import { fmtDateTime } from '../format.js';
import { showToast } from '../toast.js';
import { classifyReturn } from './inheritance.js';
import { prettyGame } from '../games.js';

export interface WingoMultipliersPageDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
  /** Whether the current scope is the provider baseline (vs. a specific integrator override). */
  isProviderScope: boolean;
}

/** Wire shape: the whole table, all thirteen bet types, every time. */
interface WingoMultipliersValue {
  version: number;
  returns: Record<string, number>;
}

/** Basis-point scale: returnBps / BPS_SCALE = total-return multiplier. */
const BPS_SCALE = 10000;

/**
 * The stake grid every chip and minimum is held to, read from the table-config
 * registry rather than typed again here.
 */
export const WINGO_STAKE_GRID = STAKE_GRANULARITY_BY_GAME.wingo!;

/**
 * A rate keeps `floor(stake × bps / 10000)` exact on that grid only when it is
 * a multiple of this step. Off it, every win is floored down. Same formula as
 * RETURN_BPS_STEP in the game package, and the step the multipliers schema
 * enforces on save.
 */
export const WINGO_STEP_BPS = BPS_SCALE / WINGO_STAKE_GRID;

type Family = 'COLOUR' | 'PURPLE' | 'NUMBER';

/**
 * Winning numbers out of ten per family. Mirrors WINNING_OUTCOMES in
 * @apx/game-wingo. The draw is uniform and not admin-tunable, so these are
 * constants and each spot's RTP is exact: `wins / 10 × returnBps / 10000`.
 */
export const WINGO_WINS: Record<Family, number> = { COLOUR: 5, PURPLE: 2, NUMBER: 1 };
export const WINGO_OUTCOMES = 10;

/**
 * Built-in total-return bps per family — mirrors DEFAULT_COLOUR / PURPLE /
 * NUMBER_RETURN_BPS in the game package. Guarded by
 * `multipliers-defaults.test.ts`.
 */
const FAMILY_DEFAULT_BPS: Record<Family, number> = {
  COLOUR: 19_600,
  PURPLE: 49_000,
  NUMBER: 98_000,
};

interface Cell {
  /** Wire bet type, e.g. `WG_NUM_7`. */
  key: string;
  family: Family;
  label: string;
}

const NUMBERS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9] as const;

/**
 * One row per bet type, in board order: the three colours, then 0-9. Exported
 * so `multipliers-defaults.test.ts` can hold the keys, the families and the
 * labels to the game package.
 */
export const WINGO_CELLS: readonly Cell[] = [
  { key: 'WG_GREEN', family: 'COLOUR', label: 'Green (1, 3, 5, 7, 9)' },
  { key: 'WG_PURPLE', family: 'PURPLE', label: 'Purple (0, 5)' },
  { key: 'WG_RED', family: 'COLOUR', label: 'Red (0, 2, 4, 6, 8)' },
  ...NUMBERS.map((n): Cell => ({ key: `WG_NUM_${n}`, family: 'NUMBER', label: `Number ${n}` })),
];

/** The heading a row sits under: the three colour bets share one. */
type Section = 'COLOUR' | 'NUMBER';

function sectionOf(family: Family): Section {
  return family === 'NUMBER' ? 'NUMBER' : 'COLOUR';
}

const SECTION_NOTE: Record<Section, string> = {
  COLOUR:
    'Green and Red each win on 5 of the 10 numbers, and pay in full on 0 and 5. Purple wins on 2 of the 10: 0 and 5.',
  NUMBER: 'An exact number wins on 1 of the 10 numbers.',
};

/** Built-in total-return bps for every bet type of this table, keyed by wire code. */
export function wingoDefaultReturns(): Record<string, number> {
  return Object.fromEntries(WINGO_CELLS.map((c) => [c.key, FAMILY_DEFAULT_BPS[c.family]]));
}

/** Exact RTP of one spot at a given rate. */
export function wingoCellRtp(family: Family, returnBps: number): number {
  return (WINGO_WINS[family] / WINGO_OUTCOMES) * (returnBps / BPS_SCALE);
}

function rtpColor(rtp: number): string {
  if (rtp > 1.0) return 'var(--error)';
  if (rtp > 0.97) return 'var(--warn)';
  return 'var(--success)';
}

/** Human hint: total-return bps → "1.96× return (0.96:1)". */
function returnHint(bps: number): string {
  const ret = bps / BPS_SCALE;
  const odds = (bps - BPS_SCALE) / BPS_SCALE;
  return `${ret.toFixed(2)}× return (${odds.toFixed(2)}:1)`;
}

/**
 * Wingo payout-multiplier editor. One total-return-bps input per bet type
 * (`returns`): 19600 = 1.96× return.
 *
 * These multipliers are the game's ONLY tunable RTP surface — one uniform draw,
 * no RNG-weight lever — so each spot's RTP is exact and shown live as you type.
 *
 * THE WHOLE TABLE IS SAVED, IN BOTH SCOPES. The schema for this game takes all
 * thirteen rates or none, so unlike the editors that submit only the rows an
 * integrator changed, a save here pins every rate for that integrator at the
 * figure on screen. The inherited/override tags still show where each figure
 * on screen came from, and per-row revert puts the provider's figure back in
 * the form. Changes ride the two-admin approval flow.
 */
export async function renderWingoMultipliersPage(
  root: HTMLElement,
  deps: WingoMultipliersPageDeps,
): Promise<void> {
  const isIntegratorScope = !deps.isProviderScope;
  const defaults = wingoDefaultReturns();
  const familyOf = new Map(WINGO_CELLS.map((c) => [c.key, c.family]));
  const keys = WINGO_CELLS.map((c) => c.key);
  const defaultValue: WingoMultipliersValue = { version: 1, returns: { ...defaults } };

  function rowHtml(c: Cell, first: boolean): string {
    const tagCell = isIntegratorScope
      ? `<td data-tag="${c.key}" class="muted" style="min-width:80px">—</td>`
      : `<td class="muted">default ${defaults[c.key]} bps</td>`;
    const revertCell = isIntegratorScope
      ? `<td><button data-revert="${c.key}" class="ghost" style="display:none;padding:2px 6px;font-size:12px">↺ revert</button></td>`
      : `<td></td>`;
    const heading = first
      ? `<tr><td colspan="7" class="muted" style="padding-top:14px;font-size:12px">${SECTION_NOTE[sectionOf(c.family)]}</td></tr>`
      : '';
    return `${heading}<tr data-row="${c.key}" data-source="inherited">
      <td><strong>${c.label}</strong><div class="muted" style="font-size:12px"><code>${c.key}</code></div></td>
      <td><input data-bet="${c.key}" type="number" min="0" max="100000000" step="${WINGO_STEP_BPS}" style="width:110px" /></td>
      <td class="muted" data-hint="${c.key}">—</td>
      <td class="muted" style="white-space:nowrap">${((WINGO_WINS[c.family] / WINGO_OUTCOMES) * 100).toFixed(0)}%</td>
      <td data-rtp="${c.key}" style="white-space:nowrap">—</td>
      ${tagCell}
      ${revertCell}
    </tr>`;
  }

  const resetAllBtn = isIntegratorScope
    ? `<button id="wgm-reset-all" class="ghost">Reset all to provider default</button>`
    : '';
  const scopeNote = isIntegratorScope
    ? ' A save stores all thirteen rates for this integrator, including the ones you did not change.'
    : '';

  root.innerHTML = `
    <h1 class="page-title">Payout Multipliers — ${prettyGame(deps.gameId)}</h1>
    <p class="page-sub">Total-return per unit stake in basis points: <code>19600 = 1.96× return</code>. Every rate must be a multiple of ${WINGO_STEP_BPS} — anything else takes wins off the ${WINGO_STAKE_GRID}-credit stake grid and floors them down, so it is refused on save.${scopeNote} Changes go through two-admin approval.</p>

    <div class="card">
      <h2>Per-bet return (bps)</h2>
      <table>
        <thead><tr><th>Bet</th><th>Return (bps)</th><th>Multiple</th><th>P(win)</th><th>RTP</th>${isIntegratorScope ? '<th>Source</th><th></th>' : '<th></th><th></th>'}</tr></thead>
        <tbody id="wgm-rows">${WINGO_CELLS.map((c, i) =>
          rowHtml(c, i === 0 || sectionOf(WINGO_CELLS[i - 1]!.family) !== sectionOf(c.family)),
        ).join('')}</tbody>
      </table>
      <p class="muted" id="wgm-headline" style="margin-top:10px">—</p>
      <div class="row" style="margin-top:14px">
        <button id="wgm-reset" class="ghost">Reset to defaults</button>
        ${resetAllBtn}
        <button id="wgm-save" class="primary">Propose change</button>
        <span id="wgm-msg" class="muted"></span>
      </div>
    </div>

    <div class="card">
      <h2>Pending approvals for this surface</h2>
      <div id="wgm-pending"><p class="muted">Loading…</p></div>
    </div>
  `;

  const rowsEl = root.querySelector<HTMLElement>('#wgm-rows')!;
  const headlineEl = root.querySelector<HTMLElement>('#wgm-headline')!;
  const msgEl = root.querySelector<HTMLElement>('#wgm-msg')!;

  // Per-row inherited values for revert (integrator scope only).
  const inheritedValues = new Map<string, number>();

  const inputs = new Map<string, HTMLInputElement>();
  for (const key of keys) {
    const el = rowsEl.querySelector<HTMLInputElement>(`input[data-bet="${key}"]`);
    if (el) inputs.set(key, el);
  }

  function refreshDerived(): void {
    let bestKey = keys[0]!;
    let best = -Infinity;
    for (const key of keys) {
      const bps = Number(inputs.get(key)?.value) || 0;
      const hintEl = rowsEl.querySelector<HTMLElement>(`[data-hint="${key}"]`);
      if (hintEl) {
        const offGrid =
          bps % WINGO_STEP_BPS !== 0
            ? ` <span style="color:var(--error)">off the ${WINGO_STAKE_GRID}-credit grid — wins round down</span>`
            : '';
        hintEl.innerHTML = `${returnHint(bps)}${offGrid}`;
      }
      const rtp = wingoCellRtp(familyOf.get(key)!, bps);
      const rtpEl = rowsEl.querySelector<HTMLElement>(`[data-rtp="${key}"]`);
      if (rtpEl) {
        const over = rtp > 1 ? ' <span style="color:var(--error)">over 100%!</span>' : '';
        rtpEl.innerHTML = `<strong style="color:${rtpColor(rtp)}">${(rtp * 100).toFixed(2)}%</strong>${over}`;
      }
      if (rtp > best) {
        best = rtp;
        bestKey = key;
      }
    }
    // Headline is the BEST bet on the board, matching the RTP hero above
    // (admin-api takes the max across bet types, not an average).
    headlineEl.innerHTML =
      `Headline RTP (best bet on the board): <strong>${WINGO_CELLS.find((c) => c.key === bestKey)!.label}</strong> at ` +
      `<strong style="color:${rtpColor(best)}">${(best * 100).toFixed(2)}%</strong> — house edge ` +
      `<strong>${((1 - best) * 100).toFixed(2)}%</strong>.`;
  }

  function markRow(key: string, source: 'override' | 'inherited'): void {
    const row = rowsEl.querySelector<HTMLElement>(`tr[data-row="${key}"]`);
    const tagEl = rowsEl.querySelector<HTMLElement>(`[data-tag="${key}"]`);
    const revertEl = rowsEl.querySelector<HTMLButtonElement>(`button[data-revert="${key}"]`);
    const isOverride = source === 'override';
    if (row) row.dataset.source = source;
    if (tagEl) {
      tagEl.textContent = source;
      tagEl.style.color = isOverride ? 'var(--accent)' : 'var(--text-dim)';
      tagEl.style.fontWeight = isOverride ? '600' : '';
    }
    if (revertEl) revertEl.style.display = isOverride ? '' : 'none';
  }

  /** Seed rows from a full value (provider scope or reset). */
  function fillForm(value: WingoMultipliersValue): void {
    for (const [key, input] of inputs) input.value = String(value.returns[key] ?? defaults[key]);
    refreshDerived();
  }

  /** Seed rows using inheritance classification (integrator scope). */
  function fillFormInherited(
    ownReturns: Record<string, number> | undefined,
    providerReturns: Record<string, number> | undefined,
  ): void {
    for (const [key, input] of inputs) {
      const classified = classifyReturn(key, ownReturns, providerReturns, defaults[key]!);
      input.value = String(classified.value);
      inheritedValues.set(
        key,
        providerReturns && Object.prototype.hasOwnProperty.call(providerReturns, key)
          ? providerReturns[key]!
          : defaults[key]!,
      );
      markRow(key, classified.source);
    }
    refreshDerived();
  }

  /**
   * Build the propose payload: every row, in both scopes. The schema refuses a
   * table with a bet type missing, so the rows an integrator left alone go in
   * at the figure they inherited.
   */
  function readForm(): WingoMultipliersValue {
    const returns: Record<string, number> = {};
    for (const [key, input] of inputs) returns[key] = Math.round(Number(input.value) || 0);
    return { version: 1, returns };
  }

  for (const [key, input] of inputs) {
    input.addEventListener('input', () => {
      if (isIntegratorScope) markRow(key, 'override');
      refreshDerived();
    });
  }

  if (isIntegratorScope) {
    for (const key of keys) {
      rowsEl.querySelector<HTMLButtonElement>(`button[data-revert="${key}"]`)?.addEventListener('click', () => {
        const input = inputs.get(key);
        if (input) input.value = String(inheritedValues.get(key) ?? defaults[key]);
        markRow(key, 'inherited');
        refreshDerived();
      });
    }

    root.querySelector<HTMLButtonElement>('#wgm-reset-all')?.addEventListener('click', () => {
      for (const [key, input] of inputs) {
        input.value = String(inheritedValues.get(key) ?? defaults[key]);
        markRow(key, 'inherited');
      }
      refreshDerived();
      msgEl.textContent = 'All bets reset to provider/baked defaults (not saved).';
      msgEl.className = 'muted';
    });
  }

  /** Re-read the surface after a save/approve, respecting the current scope. */
  async function reload(): Promise<void> {
    if (isIntegratorScope) {
      const inh = await deps.client.getConfigInheritance(deps.integratorId, deps.gameId);
      const own = inh.ownOverride.multipliers as Partial<WingoMultipliersValue> | undefined;
      const prov = inh.providerDefault.multipliers as Partial<WingoMultipliersValue> | undefined;
      fillFormInherited(own?.returns, prov?.returns);
      return;
    }
    const { value } = await deps.client.getSurface(deps.integratorId, deps.gameId, 'multipliers');
    const stored = value as Partial<WingoMultipliersValue> | null;
    fillForm(stored?.returns ? { version: 1, returns: stored.returns } : defaultValue);
  }

  await reload();

  root.querySelector<HTMLButtonElement>('#wgm-reset')!.addEventListener('click', () => {
    fillForm(defaultValue);
    if (isIntegratorScope) for (const key of keys) markRow(key, 'override');
    msgEl.textContent = 'Reset to module defaults (not saved).';
    msgEl.className = 'muted';
  });

  root.querySelector<HTMLButtonElement>('#wgm-save')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    const parsed = wingoMultipliersSchema.safeParse(readForm());
    if (!parsed.success) {
      msgEl.textContent = parsed.error.errors
        .map((e) => `${e.path.join('.')}: ${e.message}`)
        .join(' • ');
      msgEl.className = 'error';
      return;
    }
    try {
      const res = await deps.client.proposeSurface(
        deps.integratorId,
        deps.gameId,
        'multipliers',
        parsed.data,
      );
      msgEl.textContent = `Pending approval ${res.approval.id}. A different admin must approve.`;
      msgEl.className = 'muted';
      await refreshPending();
    } catch (err) {
      msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      msgEl.className = 'error';
    }
  });

  async function refreshPending(): Promise<void> {
    const all = await deps.client.listApprovals(deps.integratorId, 'pending');
    const subset = all.filter((a) => a.surface === 'multipliers' && a.gameId === deps.gameId);
    const container = root.querySelector<HTMLElement>('#wgm-pending')!;
    if (subset.length === 0) {
      container.innerHTML = '<p class="muted">No pending changes.</p>';
      return;
    }
    container.innerHTML = `<table>
      <thead><tr><th>Created</th><th>Proposer</th><th></th></tr></thead>
      <tbody>${subset
        .map(
          (a) => `<tr>
            <td>${fmtDateTime(a.createdAt)}</td>
            <td><code>${a.proposerUserId.slice(0, 8)}…</code></td>
            <td>
              <button data-approve="${a.id}">Approve</button>
              <button data-reject="${a.id}" class="ghost">Reject</button>
            </td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>`;
    container.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          await deps.client.approve(btn.dataset.approve!);
          msgEl.textContent = 'Approved — payout multipliers now live.';
          msgEl.className = 'muted';
          await refreshPending();
          await reload();
        } catch (err) {
          showToast(err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err), 'error');
        }
      });
    });
    container.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const reason = await askPrompt('Rejection reason?');
        if (!reason) return;
        try {
          await deps.client.reject(btn.dataset.reject!, reason);
          await refreshPending();
        } catch (err) {
          showToast(err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err), 'error');
        }
      });
    });
  }

  await refreshPending();
}
